import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Check, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TripFormDialog } from "./TripFormDialog";

const Requested = ({ w, date, index, formOpen, setFormOpen }) => {
  const { data: request } = useQuery({ queryKey: ["trip-request", w.id], queryFn: () => api.myTripRequest(w.id) });
  const booked = w.arrange === "booked";
  return (
    <div className="mt-2 pl-[3.25rem] flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <p className="flex items-center gap-1.5 text-[11px] text-emerald-300" data-testid={`arrange-${w.arrange}-${index}`}>
        <Check size={12} /> {booked ? "Booked with our travel team" : "Our travel team will contact you about this trip"}
      </p>
      {!booked && request?.request_id && (
        <button onClick={() => setFormOpen(true)} className="flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-200 transition-colors" data-testid={`trip-form-open-${index}`}>
          <ClipboardList size={12} /> {request.form ? "Edit travel form" : "Fill in travel form"}
        </button>
      )}
      {request?.request_id && <TripFormDialog open={formOpen} onOpenChange={setFormOpen} request={request} country={w.country} date={date} end={w.end_date} />}
    </div>
  );
};

export const ArrangeOffer = ({ w, b, P, index }) => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const plan = P.planFor(w.country);
  const date = w.target_date || plan?.start_date || "";
  if (!date || w.arrange === "dismissed") return null;

  if (w.arrange === "requested" || w.arrange === "booked")
    return <Requested w={w} date={date} index={index} formOpen={formOpen} setFormOpen={setFormOpen} />;

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.requestTrip(w.id);
      qc.setQueryData(["trip-request", w.id], r);
      await qc.invalidateQueries({ queryKey: ["state"] });
      toast.success(`Thanks! Our travel team will be in touch about ${w.country}`);
      setFormOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 ml-[3.25rem] rounded-xl border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 animate-rise" data-testid={`arrange-offer-${index}`}>
      <p className="flex items-center gap-2 text-xs text-slate-200 flex-1 min-w-[12rem]">
        <Sparkles size={14} className="text-emerald-300 shrink-0" />
        <span>Going to {w.country}? <span className="text-slate-400">Our travel team can arrange and execute the whole trip for you.</span></span>
      </p>
      <div className="flex items-center gap-3">
        <button onClick={() => b.patchWish(w.id, { arrange: "dismissed" }, "Offer hidden for this trip")} className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors" data-testid={`arrange-dismiss-${index}`}>No thanks</button>
        <button onClick={accept} disabled={busy} className="text-xs font-medium rounded-full bg-emerald-400 text-[#04120c] px-3 py-1 hover:bg-emerald-300 disabled:opacity-60 transition-colors" data-testid={`arrange-accept-${index}`}>
          {busy ? "Sending…" : "Yes, contact me"}
        </button>
      </div>
    </div>
  );
};
